<template>
    <div ref="root">
        <div v-if="modelValue !== null">
            <img
                v-if="modelValue.method === DocumentSignatureMethod.DRAWN"
                :src="modelValue.value"
                :alt="fieldLabel"
                class="border-1 surface-border max-w-full"
                :class="
                    initials ? 'h-[150px] w-[150px]' : 'h-[175px] w-[350px]'
                "
            />
            <div
                v-else
                class="document-signature-text border-1 surface-border flex max-w-full items-center justify-center bg-white p-3 text-center text-black"
                :class="
                    initials
                        ? 'min-h-[150px] w-[150px]'
                        : 'min-h-[175px] w-[350px]'
                "
            >
                {{ modelValue.value }}
            </div>
            <div class="flex gap-2">
                <prime-button
                    severity="secondary"
                    outlined
                    :label="changeLabel"
                    @click="openDialog"
                />

                <inline-message
                    v-if="invalidData"
                    class="js-RequiredFormInput"
                >
                    {{ invalidWarning }}
                </inline-message>
            </div>
        </div>

        <div
            v-else
            class="flex gap-2"
        >
            <prime-button
                :label="startLabel"
                @click="openDialog"
            />

            <inline-message
                v-if="showErrors"
                class="js-RequiredFormInput"
            >
                {{ requiredWarning }}
            </inline-message>
        </div>

        <prime-dialog
            v-model:visible="display"
            :header="fieldLabel"
            :modal="true"
            :pt="{
                root: {
                    class: [
                        'w-full h-max-md:h-full max-h-full !rounded-none md:h-auto',
                        initials ? 'md:!w-[450px]' : 'md:!w-[750px]'
                    ]
                },
                header: {
                    class: '!dark:bg-slate-800 !bg-slate-100 !rounded-none'
                },
                content: {
                    class: '!dark:bg-slate-800 !bg-slate-100 !rounded-none flex-1'
                },
                footer: {
                    class: '!dark:bg-slate-800 !bg-slate-100 !rounded-none'
                }
            }"
            :pt-options="{ mergeProps: true }"
        >
            <div
                v-if="
                    signatureMode === PlanDocumentSignatureMode.DRAW_OR_TYPE ||
                    signatureMode === PlanDocumentSignatureMode.TYPE_OR_DRAW
                "
                class="mb-4 flex gap-6"
                role="radiogroup"
                :aria-label="t('purchaseContract.documentSignatureMethod')"
            >
                <div class="flex items-center gap-2">
                    <radio-button
                        v-model="method"
                        :input-id="`${methodGroupName}-draw`"
                        :name="methodGroupName"
                        :value="DocumentSignatureMethod.DRAWN"
                    />
                    <label
                        :for="`${methodGroupName}-draw`"
                        class="cursor-pointer"
                    >
                        {{ t('purchaseContract.documentDraw') }}
                    </label>
                </div>
                <div class="flex items-center gap-2">
                    <radio-button
                        v-model="method"
                        :input-id="`${methodGroupName}-type`"
                        :name="methodGroupName"
                        :value="DocumentSignatureMethod.TYPED"
                    />
                    <label
                        :for="`${methodGroupName}-type`"
                        class="cursor-pointer"
                    >
                        {{ t('purchaseContract.documentType') }}
                    </label>
                </div>
            </div>

            <app-signature-pad
                v-if="method === DocumentSignatureMethod.DRAWN"
                :canvas-class="
                    initials
                        ? 'aspect-square w-full max-w-[300px]'
                        : 'aspect-[2/1] w-full'
                "
                :initial-data="drawnValue"
                :help-text="
                    initials
                        ? t('purchaseContract.documentDrawInitialsHelp')
                        : t('purchaseContract.documentDrawSignatureHelp')
                "
                @data="drawnValue = $event"
            />

            <div
                v-else
                class="space-y-4"
            >
                <label class="block space-y-2">
                    <span class="block">
                        {{
                            initials
                                ? t('purchaseContract.documentTypeInitials')
                                : t('purchaseContract.documentTypeName')
                        }}
                    </span>
                    <input-text
                        v-model="typedValue"
                        class="w-full"
                        autocomplete="off"
                        autofocus
                        @keydown.enter.prevent="handleAccept"
                    />
                </label>
                <div>
                    <div class="mb-2">
                        {{ t('purchaseContract.documentSignaturePreview') }}
                    </div>
                    <div
                        class="document-signature-text border-1 surface-border flex min-h-[150px] items-center justify-center bg-white p-3 text-center text-black"
                        aria-live="polite"
                    >
                        {{ typedValue.trim() }}
                    </div>
                </div>
            </div>

            <template #footer>
                <div class="flex justify-end gap-4">
                    <prime-button
                        :label="t('cancel')"
                        text
                        @click="display = false"
                    />
                    <prime-button
                        :label="
                            initials
                                ? t('purchaseContract.documentUseInitials')
                                : t('purchaseContract.documentUseSignature')
                        "
                        icon="pi pi-check"
                        :disabled="currentValue === ''"
                        @click="handleAccept"
                    />
                </div>
            </template>
        </prime-dialog>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, useId } from 'vue'
import PrimeButton from 'primevue/button'
import PrimeDialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import InlineMessage from 'primevue/inlinemessage'
import RadioButton from 'primevue/radiobutton'
import { useI18n } from 'vue-i18n'
import AppSignaturePad from '~/components/ContentTemplate/AppSignaturePad.vue'
import type {
    DocumentSignatureInput,
    DocumentSignatureMode
} from '~/resources/graphql/graphql'
import {
    DocumentSignatureMethod,
    PlanDocumentSignatureMode
} from '../../../resources/app/generated/enums'

const props = withDefaults(
    defineProps<{
        modelValue: DocumentSignatureInput | null
        signatureMode: DocumentSignatureMode
        initials?: boolean
        showErrors: boolean
        invalidData: boolean
    }>(),
    { initials: false }
)

const emit = defineEmits<{
    (e: 'update:modelValue', payload: DocumentSignatureInput): void
}>()

const { t } = useI18n({ useScope: 'global' })
const methodGroupName = `signature-method-${useId()}`
const root = ref<HTMLDivElement>()
const display = ref(false)
const method = ref<DocumentSignatureInput['method']>(
    DocumentSignatureMethod.DRAWN
)
const drawnValue = ref('')
const typedValue = ref('')
const currentValue = computed(() =>
    method.value === DocumentSignatureMethod.TYPED
        ? typedValue.value.trim()
        : drawnValue.value
)
const fieldLabel = computed(() =>
    props.initials
        ? t('purchaseContract.documentSection.initials')
        : t('purchaseContract.documentSection.signature')
)
const changeLabel = computed(() =>
    props.initials
        ? t('purchaseContract.documentSection.changeInitials')
        : t('purchaseContract.documentSection.changeSignature')
)
const startLabel = computed(() =>
    props.initials
        ? t('purchaseContract.documentSection.clickToInitial')
        : t('purchaseContract.documentSection.clickToSign')
)
const requiredWarning = computed(() =>
    props.initials
        ? t('purchaseContract.documentSection.initialsRequiredWarning')
        : t('purchaseContract.documentSection.signatureRequiredWarning')
)
const invalidWarning = computed(() => {
    if (props.modelValue?.method !== DocumentSignatureMethod.DRAWN) {
        return requiredWarning.value
    }

    return props.initials
        ? t('purchaseContract.documentSection.initialsMissingWarning')
        : t('purchaseContract.documentSection.signatureMissingWarning')
})

function openDialog() {
    method.value =
        props.modelValue?.method ??
        (props.signatureMode === PlanDocumentSignatureMode.TYPE_ONLY ||
        props.signatureMode === PlanDocumentSignatureMode.TYPE_OR_DRAW
            ? DocumentSignatureMethod.TYPED
            : DocumentSignatureMethod.DRAWN)
    drawnValue.value =
        props.modelValue?.method === DocumentSignatureMethod.DRAWN
            ? props.modelValue.value
            : ''
    typedValue.value =
        props.modelValue?.method === DocumentSignatureMethod.TYPED
            ? props.modelValue.value
            : ''
    display.value = true
}

function handleAccept() {
    if (currentValue.value === '') {
        return
    }

    emit('update:modelValue', {
        method: method.value,
        value: currentValue.value
    })
    display.value = false
    nextTick().then(() => {
        root.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
}
</script>
